import React, { useEffect, useState } from "react";
import { Navigate, Link, useLocation } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { api, fmt } from "../lib/api";
import { toast } from "sonner";
import { Shield, Users, CreditCard, TrendingUp, Trophy, RefreshCw, Wifi, WifiOff, Plus, StopCircle, CheckCircle2, MessageSquare, FileCheck, Banknote } from "lucide-react";
import { AdminChatPanel, AdminKycPanel, AdminCashoutPanel } from "./AdminSupport";

export default function AdminDashboard() {
  const { user } = useApp();
  const loc = useLocation();
  const [tab, setTab] = useState(loc.pathname.endsWith("/sports") ? "sports" : "overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [bets, setBets] = useState([]);
  const [matches, setMatches] = useState([]);
  const [oddsStatus, setOddsStatus] = useState(null);
  const [oddsLoading, setOddsLoading] = useState(false);
  const [tournaments, setTournaments] = useState([]);

  const load = async () => {
    try {
      const [s, u, p, b, m, o, t] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/users"),
        api.get("/admin/payments"), api.get("/admin/bets"),
        api.get("/admin/matches"), api.get("/admin/odds/status"),
        api.get("/tournaments"),
      ]);
      setStats(s.data); setUsers(u.data); setPayments(p.data); setBets(b.data); setMatches(m.data); setOddsStatus(o.data); setTournaments(t.data);
    } catch (e) { /* not admin */ }
  };

  const refreshOdds = async () => {
    setOddsLoading(true);
    try {
      const r = await api.post("/admin/odds/refresh");
      setOddsStatus(prev => ({...prev, status: r.data.status}));
      toast.success(r.data.status?.message || "Refreshed");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setOddsLoading(false); }
  };

  const resetCounter = async () => {
    try { await api.post("/admin/odds/reset-counter"); toast.success("Counter reset"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const sendTestAlert = async () => {
    try {
      const r = await api.post("/admin/alerts/test");
      toast.success(`Test email sent to ${r.data.recipient}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const endTournament = async (id) => {
    if (!window.confirm("End this tournament now? Final leaderboard will be frozen.")) return;
    try { await api.post(`/admin/tournaments/${id}/end`); toast.success("Tournament ended"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const settleTournament = async (id) => {
    const note = window.prompt("Fulfillment note (e.g. 'Steam codes sent 2026-06-29'):");
    if (!note) return;
    try { await api.post(`/admin/tournaments/${id}/settle`, { fulfillment_note: note }); toast.success("Tournament marked settled"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const createTournament = async () => {
    const name = window.prompt("Tournament name:", "Custom Tournament");
    if (!name) return;
    const days = parseInt(window.prompt("Duration in days:", "7") || "7", 10);
    const start = new Date().toISOString();
    const end = new Date(Date.now() + days * 86400000).toISOString();
    const prizes = [
      {place: 1, prize: "Steam Gift Card 50€", value: 50},
      {place: 2, prize: "Steam Gift Card 25€", value: 25},
      {place: 3, prize: "Steam Gift Card 15€", value: 15},
    ];
    try {
      await api.post("/admin/tournaments", { name, description: "Admin-created tournament", start, end, prizes, metric: "net_winnings" });
      toast.success("Tournament created");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  useEffect(()=>{ if(user?.is_admin) load(); }, [user]);

  if (!user) return <Navigate to="/login" replace/>;
  if (!user.is_admin) return <Navigate to="/lobby" replace/>;

  const settle = async (matchId, result) => {
    try {
      const r = await api.post(`/admin/sports/settle/${matchId}`, { result });
      toast.success(`Settled · ${r.data.settled} bets · ${r.data.total_paid.toLocaleString()} paid`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-10">
      <h1 className="font-serif text-4xl gold-text mb-2 flex items-center gap-3"><Shield/>Admin Console</h1>
      <p className="text-white/40 mb-6 text-sm">Restricted to {user.email}</p>

      <div className="flex gap-2 mb-6 flex-wrap">
        {["overview","users","payments","sports","odds","tournaments","chat","kyc","cashout"].map(k=>(
          <button key={k} data-testid={`admin-tab-${k}`} onClick={()=>setTab(k)} className={`px-4 py-2 rounded-md text-xs uppercase tracking-widest ${tab===k?"btn-gold":"btn-ghost"}`}>{k}</button>
        ))}
      </div>

      {tab==="overview" && stats && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={<Users size={18}/>} label="Users" value={stats.users}/>
          <Stat icon={<TrendingUp size={18}/>} label="Rounds played" value={stats.rounds}/>
          <Stat icon={<Trophy size={18}/>} label="Sports bets" value={stats.sports_bets}/>
          <Stat icon={<CreditCard size={18}/>} label="Paid Tx" value={`${stats.payments_paid}/${stats.payments_total}`}/>
          <div className="lux-card p-5 sm:col-span-2 lg:col-span-4">
            <div className="text-xs uppercase tracking-widest text-[#d4af37] mb-3">Revenue (paid transactions)</div>
            <table className="w-full text-sm">
              <thead><tr className="text-white/40 text-xs uppercase"><th className="text-left p-2">Currency</th><th className="text-right p-2">Count</th><th className="text-right p-2">Total</th></tr></thead>
              <tbody>
                {stats.revenue.map((r,i)=>(
                  <tr key={i} className="border-t border-white/5"><td className="p-2 uppercase">{r._id}</td><td className="p-2 text-right">{r.count}</td><td className="p-2 text-right text-[#d4af37]">{r.total.toLocaleString()} {r._id?.toUpperCase()}</td></tr>
                ))}
                {stats.revenue.length===0 && <tr><td colSpan={3} className="p-4 text-center text-white/40">No paid transactions yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab==="users" && (
        <div className="lux-card overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-black/40 text-white/50 text-xs uppercase">
              <tr><th className="text-left p-3">Email</th><th className="text-left p-3">Name</th><th className="text-right p-3">HUF</th><th className="text-right p-3">EUR</th><th className="text-left p-3">VIP</th><th className="text-right p-3">Wager (HUF)</th><th className="text-left p-3">Auth</th></tr>
            </thead>
            <tbody>
              {users.map(u=>(
                <tr key={u.user_id} className="border-t border-white/5" data-testid={`admin-user-${u.user_id}`}>
                  <td className="p-3">{u.email} {u.is_admin && <span className="text-[#d4af37] text-[10px] ml-1">ADMIN</span>}</td>
                  <td className="p-3">{u.name}</td>
                  <td className="p-3 text-right">{fmt(u.balance_huf, "HUF")}</td>
                  <td className="p-3 text-right">{fmt(u.balance_eur, "EUR")}</td>
                  <td className="p-3" style={{color:u.vip_tier.color}}>{u.vip_tier.name}</td>
                  <td className="p-3 text-right">{Math.round(u.total_wager_huf||0).toLocaleString()}</td>
                  <td className="p-3 text-white/50">{u.auth_provider}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab==="payments" && (
        <div className="lux-card overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-black/40 text-white/50 text-xs uppercase">
              <tr><th className="text-left p-3">Date</th><th className="text-left p-3">User</th><th className="text-left p-3">Package</th><th className="text-right p-3">Amount</th><th className="text-right p-3">Credits</th><th className="text-left p-3">Status</th></tr>
            </thead>
            <tbody>
              {payments.map((p,i)=>(
                <tr key={i} className="border-t border-white/5" data-testid={`admin-payment-${i}`}>
                  <td className="p-3 text-white/50 text-xs">{new Date(p.created_at).toLocaleString()}</td>
                  <td className="p-3">{p.user_email}</td>
                  <td className="p-3 text-xs">{p.package_id}</td>
                  <td className="p-3 text-right">{p.amount.toLocaleString()} {p.currency.toUpperCase()}</td>
                  <td className="p-3 text-right text-[#d4af37]">{p.credits.toLocaleString()} {p.balance_currency}</td>
                  <td className="p-3">
                    <span className={`text-xs uppercase ${p.payment_status==="paid"?"text-emerald-400":p.payment_status==="pending"?"text-yellow-400":"text-red-400"}`}>{p.payment_status}</span>
                  </td>
                </tr>
              ))}
              {payments.length===0 && <tr><td colSpan={6} className="p-6 text-center text-white/40">No payments yet</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab==="sports" && (
        <div className="space-y-6">
          <div>
            <h3 className="font-serif text-xl mb-3">Open matches · settle to release payouts</h3>
            <div className="space-y-3">
              {matches.filter(m=>!m.settled).map(m=>(
                <div key={m.id} className="lux-card p-4 flex flex-wrap items-center gap-4" data-testid={`admin-match-${m.id}`}>
                  <div className="flex-1 min-w-[200px]">
                    <div className="text-xs text-[#d4af37] uppercase tracking-widest">{m.league}</div>
                    <div className="font-serif">{m.home} vs {m.away}</div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button data-testid={`settle-${m.id}-home`} onClick={()=>settle(m.id,"home")} className="btn-ghost px-3 py-2 rounded text-xs">Home wins</button>
                    {m.odds.draw && <button data-testid={`settle-${m.id}-draw`} onClick={()=>settle(m.id,"draw")} className="btn-ghost px-3 py-2 rounded text-xs">Draw</button>}
                    <button data-testid={`settle-${m.id}-away`} onClick={()=>settle(m.id,"away")} className="btn-ghost px-3 py-2 rounded text-xs">Away wins</button>
                    <button data-testid={`settle-${m.id}-void`} onClick={()=>settle(m.id,"void")} className="btn-burgundy px-3 py-2 rounded text-xs">Void</button>
                  </div>
                </div>
              ))}
              {matches.filter(m=>!m.settled).length===0 && <div className="text-white/40">All settled.</div>}
            </div>
          </div>
          <div>
            <h3 className="font-serif text-xl mb-3">All bets</h3>
            <div className="lux-card overflow-hidden overflow-x-auto">
              <table className="w-full text-sm min-w-[700px]">
                <thead className="bg-black/40 text-white/50 text-xs uppercase">
                  <tr><th className="text-left p-3">User</th><th className="text-left p-3">Match</th><th className="text-left p-3">Pick</th><th className="text-right p-3">Stake</th><th className="text-right p-3">Odds</th><th className="text-left p-3">Status</th></tr>
                </thead>
                <tbody>
                  {bets.map(b=>(
                    <tr key={b.bet_id} className="border-t border-white/5">
                      <td className="p-3">{b.user_email}</td>
                      <td className="p-3 text-xs">{b.match_label}</td>
                      <td className="p-3 uppercase text-[#d4af37]">{b.pick}</td>
                      <td className="p-3 text-right">{fmt(b.stake, b.currency)}</td>
                      <td className="p-3 text-right">{b.odds?.toFixed?.(2)}</td>
                      <td className="p-3 text-xs uppercase">{b.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {tab==="odds" && oddsStatus && (
        <div className="space-y-6">
          <div className="lux-card p-6">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
              <div>
                <h3 className="font-serif text-xl flex items-center gap-2">
                  {oddsStatus.api_key_configured ? <Wifi className="text-emerald-400" size={20}/> : <WifiOff className="text-yellow-400" size={20}/>}
                  Odds Feed Status
                </h3>
                <p className="text-xs text-white/40 mt-1">Source: the-odds-api.com · Refresh every {oddsStatus.refresh_minutes} min</p>
              </div>
              <div className="flex gap-2">
                <button data-testid="odds-refresh-now" onClick={refreshOdds} disabled={oddsLoading} className="btn-gold px-5 py-2.5 rounded-md text-xs flex items-center gap-2"><RefreshCw size={14} className={oddsLoading?"animate-spin":""}/>Refresh now</button>
                <button data-testid="odds-reset-counter" onClick={resetCounter} className="btn-ghost px-4 py-2.5 rounded-md text-xs">Reset month</button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-black/40 rounded-md border border-white/10 p-4">
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">API key</div>
                <div data-testid="odds-key-status" className="font-serif text-lg" style={{color: oddsStatus.api_key_configured ? "#34d399" : "#facc15"}}>
                  {oddsStatus.api_key_configured ? "Configured" : "Missing"}
                </div>
                {!oddsStatus.api_key_configured && <div className="text-[10px] text-white/40 mt-1">Add ODDS_API_KEY to /app/backend/.env</div>}
              </div>
              <div className="bg-black/40 rounded-md border border-white/10 p-4">
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Open matches</div>
                <div className="font-serif text-2xl text-[#d4af37]">{oddsStatus.open_matches}</div>
              </div>
              <div className="bg-black/40 rounded-md border border-white/10 p-4">
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Settled</div>
                <div className="font-serif text-2xl">{oddsStatus.settled_matches}</div>
              </div>
              <div className="bg-black/40 rounded-md border border-white/10 p-4">
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Quota usage</div>
                <div className="font-serif text-2xl" data-testid="odds-quota">
                  <span className={oddsStatus.monthly_quota.used > oddsStatus.monthly_quota.limit*0.8 ? "text-red-400" : "text-emerald-400"}>{oddsStatus.monthly_quota.used}</span>
                  <span className="text-white/40 text-base"> / {oddsStatus.monthly_quota.limit}</span>
                </div>
                <div className="w-full h-1.5 bg-white/10 rounded-full mt-2 overflow-hidden">
                  <div className="h-full bg-[#d4af37]" style={{width: `${Math.min(100, oddsStatus.monthly_quota.used/oddsStatus.monthly_quota.limit*100)}%`}}/>
                </div>
              </div>
            </div>
            <div className="mt-5 grid lg:grid-cols-2 gap-4 text-sm">
              <div className="bg-black/40 rounded-md border border-white/10 p-4">
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Last attempt</div>
                <div data-testid="odds-last-attempt" className="text-white/80">{oddsStatus.status?.last_attempt_at ? new Date(oddsStatus.status.last_attempt_at).toLocaleString() : "—"}</div>
                <div className="text-xs text-white/40 mt-1">{oddsStatus.status?.message || ""}</div>
              </div>
              <div className="bg-black/40 rounded-md border border-white/10 p-4">
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Last success</div>
                <div data-testid="odds-last-success" className="text-emerald-400">{oddsStatus.status?.last_success_at ? new Date(oddsStatus.status.last_success_at).toLocaleString() : "Never"}</div>
                <div className="text-xs text-white/40 mt-1">{oddsStatus.status?.matches_upserted ? `${oddsStatus.status.matches_upserted} matches upserted` : ""}</div>
              </div>
            </div>
            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Sports tracked</div>
              <div className="flex flex-wrap gap-2">
                {oddsStatus.sports_tracked.map(s => (
                  <span key={s} className="px-3 py-1 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 text-[#d4af37] text-xs">{s}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {tab==="tournaments" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h3 className="font-serif text-xl flex items-center gap-2"><Trophy className="text-[#d4af37]"/>Tournaments</h3>
            <button data-testid="admin-create-tournament" onClick={createTournament} className="btn-gold px-5 py-2.5 rounded-md text-xs flex items-center gap-2"><Plus size={14}/>Create Tournament</button>
          </div>
          <div className="space-y-3">
            {tournaments.length === 0 && <div className="text-white/40 text-center py-10">No tournaments yet.</div>}
            {tournaments.map(t => {
              const now = new Date();
              const start = new Date(t.start); const end = new Date(t.end);
              const active = !t.winners_settled && t.status !== "ended" && start <= now && now <= end;
              const ended = end < now || t.status === "ended";
              return (
                <div key={t.id} className="lux-card p-5" data-testid={`admin-tournament-${t.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex-1 min-w-[240px]">
                      <div className="flex items-center gap-2">
                        <span className="font-serif text-lg">{t.name}</span>
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                          active ? "bg-emerald-400/15 text-emerald-300 border border-emerald-400/30"
                                 : t.winners_settled ? "bg-[#d4af37]/15 text-[#d4af37] border border-[#d4af37]/30"
                                 : ended ? "bg-red-400/15 text-red-300 border border-red-400/30"
                                 : "bg-white/10 text-white/60 border border-white/10"
                        }`}>
                          {t.winners_settled ? "SETTLED" : active ? "ACTIVE" : ended ? "ENDED" : "SCHEDULED"}
                        </span>
                      </div>
                      <div className="text-xs text-white/40 mt-1">{t.description}</div>
                      <div className="text-xs text-white/50 mt-2">
                        {start.toLocaleDateString()} → {end.toLocaleDateString()} · metric: <span className="text-[#d4af37]">{t.metric}</span> · {t.prizes?.length || 0} prizes
                      </div>
                      {t.fulfillment_note && <div className="text-xs text-emerald-400 mt-1">✓ {t.fulfillment_note}</div>}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {active && (
                        <button data-testid={`end-tournament-${t.id}`} onClick={()=>endTournament(t.id)} className="btn-burgundy px-4 py-2 rounded text-xs flex items-center gap-1"><StopCircle size={12}/>End now</button>
                      )}
                      {ended && !t.winners_settled && (
                        <button data-testid={`settle-tournament-${t.id}`} onClick={()=>settleTournament(t.id)} className="btn-gold px-4 py-2 rounded text-xs flex items-center gap-1"><CheckCircle2 size={12}/>Mark settled</button>
                      )}
                    </div>
                  </div>
                  {t.final_leaderboard && t.final_leaderboard.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-white/5">
                      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Final winners</div>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1 text-xs">
                        {t.final_leaderboard.slice(0, 10).map(w => (
                          <div key={w.place} className="flex items-center gap-2 bg-black/30 rounded px-2 py-1">
                            <span className="text-[#d4af37] w-5">{w.place}.</span>
                            <span className="flex-1 truncate">{w.name}</span>
                            <span className="text-white/40">{Math.round(w.score).toLocaleString()} GC</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {tab==="chat" && (
        <div>
          <div className="flex items-center gap-2 mb-4"><MessageSquare className="text-[#d4af37]"/><h3 className="font-serif text-xl">Live Chat · Player Support</h3></div>
          <AdminChatPanel/>
        </div>
      )}
      {tab==="kyc" && (
        <div>
          <div className="flex items-center gap-2 mb-4"><FileCheck className="text-[#d4af37]"/><h3 className="font-serif text-xl">KYC Reviews</h3></div>
          <AdminKycPanel/>
        </div>
      )}
      {tab==="cashout" && (
        <div>
          <div className="flex items-center gap-2 mb-4"><Banknote className="text-[#d4af37]"/><h3 className="font-serif text-xl">Cashout Requests</h3></div>
          <AdminCashoutPanel/>
        </div>
      )}
    </div>
  );
}

function Stat({icon, label, value}) {
  return (
    <div className="lux-card p-5">
      <div className="text-[#d4af37] mb-2 flex items-center gap-2 text-xs uppercase tracking-widest">{icon}{label}</div>
      <div className="font-serif text-3xl">{value}</div>
    </div>
  );
}
